import {
  FlashIcon,
  FoodIcon,
  ScreenIcon,
  SeatRegularIcon,
  WifiIcon
} from "@bookingcom/bui-assets-react/streamline/index.js";
import { SVG } from "@bookingcom/bui-react/components/Icon/Icon.types";
import { t, useI18n } from "@bookingcom/lingojs-react";
import {
  UIAmenity,
  UIAmenityAircraft,
  UIAmenityCategory,
  UIFlightSegmentTimelined,
  UITimelineLeg
} from "@flights/types";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import { getFlightDetailsSegments } from "../store/flightDetails/selectors";

const useLegAmenities = (amenities: UITimelineLeg["amenities"] | undefined) => {
  const i18n = useI18n();

  const iconSvg = useCallback((category: UIAmenityCategory) => {
    switch (category) {
      case "WIFI":
        return WifiIcon;
      case "FOOD":
        return FoodIcon;
      case "POWER":
        return FlashIcon;
      case "ENTERTAINMENT":
        return ScreenIcon;
      case "SEAT":
        return SeatRegularIcon;
    }

    return undefined;
  }, []);

  const getCopy = useCallback(
    (amenity: UIAmenity[number]) => {
      switch (amenity.category) {
        case "WIFI":
          if (amenity.cost === "free") return i18n.trans(t("flights_fd_route_happy_wifi_included"));
          if (amenity.cost === "paid") return i18n.trans(t("flights_fd_route_happy_wifi_paid_or_both"));
          if (amenity.cost === "free_or_paid") return i18n.trans(t("flights_fd_route_happy_wifi_paid_or_both"));
          break;

        case "FOOD":
          if (amenity.cost === "free") return i18n.trans(t("flights_fd_route_happy_meal_any_included"));
          if (amenity.cost === "paid") return i18n.trans(t("flights_fd_route_happy_meal_any_paid"));
          break;

        case "POWER":
          if (amenity.cost === "free" && amenity.type) {
            if (amenity.type.includes("power_outlet") && amenity.type.includes("usb_port")) {
              return i18n.trans(t("flights_fd_route_happy_power_both_included"));
            }
            if (amenity.type.includes("power_outlet")) {
              return i18n.trans(t("flights_fd_route_happy_outlets_included"));
            }
            if (amenity.type.includes("usb_port")) {
              return i18n.trans(t("flights_fd_route_happy_usb_included"));
            }
          }
          if (amenity.cost === "paid") return i18n.trans(t("flights_fd_route_happy_power_any_paid"));
          break;

        case "ENTERTAINMENT":
          if (amenity.cost === "free") return i18n.trans(t("flights_fd_route_happy_entertainment_included"));
          if (amenity.cost === "paid") return i18n.trans(t("flights_fd_route_happy_entertainment_paid"));
          break;

        case "SEAT":
          if (amenity.legroom === "less")
            return i18n.trans(
              t("flights_fd_route_happy_seat_legroom_below_average", {
                variables: { seat_pitch: `${amenity.pitch} ${amenity.pitchUnit}` }
              })
            );
          if (amenity.legroom === "standard")
            return i18n.trans(
              t("flights_fd_route_happy_seat_legroom_average", {
                variables: { seat_pitch: `${amenity.pitch} ${amenity.pitchUnit}` }
              })
            );
          if (amenity.legroom === "more")
            return i18n.trans(
              t("flights_fd_route_happy_seat_legroom__above_average", {
                variables: { seat_pitch: `${amenity.pitch} ${amenity.pitchUnit}` }
              })
            );
          break;
      }

      return undefined;
    },
    [i18n]
  );

  const getAmenities = useCallback(() => {
    const data: Array<{ svg: SVG; copy: string }> = [];

    if (!amenities) return data;
    if (amenities.length === 0) return data;

    const orderMap: Partial<Record<UIAmenityCategory, number>> = {
      WIFI: 0,
      SEAT: 1,
      FOOD: 2,
      POWER: 3,
      ENTERTAINMENT: 4
    };

    amenities.forEach((amenity) => {
      if (amenity.category === "AIRCRAFT") return;

      const svg = iconSvg(amenity.category);
      const copy = getCopy(amenity);
      const order = orderMap[amenity.category];
      if (svg && copy && typeof order === "number") {
        data[order] = {
          svg,
          copy
        };
      }
    });

    return data.filter(Boolean);
  }, [amenities, getCopy, iconSvg]);

  const getAircraftModel = useCallback(() => {
    if (!amenities) return;
    // If we update Typescript version this assertion won't be neccessary.
    return (amenities.find((a) => a.category === "AIRCRAFT") as UIAmenityAircraft | undefined)?.model;
  }, [amenities]);

  return { getAmenities, getAircraftModel };
};

// This hook is used to inject amenities data from "Flight Details" to other data sources.
// For instance, we show amenities data on Check & Pay but amenties are not available from "Order".
export const useInjectAmenitiesToTimeline = () => {
  const segments = useSelector(getFlightDetailsSegments);

  return useCallback(
    (timelinedSegments: UIFlightSegmentTimelined[]) => {
      if (!segments) return timelinedSegments;

      return timelinedSegments.map((s, sIndex) => {
        // We need to skip items from timeline when their type is not "FLIGHT"
        // hence we have this counter.
        let timelineIndex = 0;

        return {
          ...s,
          legs: s.legs.map((l, lIndex) => ({ ...l, amenities: segments?.[sIndex].legs?.[lIndex]?.amenities || [] })),
          timeline: s.timeline.map((t) => {
            if (t.type !== "FLIGHT") return t;

            const amenities = segments?.[sIndex].legs?.[timelineIndex]?.amenities || [];
            timelineIndex++;

            return {
              ...t,
              amenities
            };
          })
        };
      });
    },
    [segments]
  );
};

export default useLegAmenities;
